import { isPermission, type Permission, resolvePermission } from "@tcg/api-client";

/**
 * Permission strings are generated from `shop_common.auth.Permission` (apps/packages/api-client/
 * src/permissions.ts) — the same `<area>.<action>` catalogue identity-service puts into tokens
 * (design/04 §14). The owner role carries the full set; there is no wildcard on the client.
 */
export type { Permission };
export { isPermission, resolvePermission };

export interface StaffUser {
  id: string;
  displayName: string;
  email: string;
  roles: string[];
  permissions: Permission[];
  /** Staff profile language at identity-service (informational — the UI choice is local). */
  locale?: string;
  totpEnabled?: boolean;
  /** Store/warehouse codes the account is limited to (empty = all). */
  locationCodes?: string[];
}

export interface Session {
  accessToken: string;
  /** Epoch milliseconds (access token). */
  expiresAt: number;
  user: StaffUser;
  /** identity-service refresh session id (marks "this device" in the sessions list). */
  sessionId?: string;
}

export interface Credentials {
  email: string;
  password: string;
}

/** What `POST /v1/auth/staff/login` (and a forced password change) asks for next. */
export type LoginStep =
  | { kind: "session"; session: Session }
  | { kind: "mfa"; mfaToken: string; expiresAt: number | null }
  | { kind: "enroll"; enrollToken: string; expiresAt: number | null }
  | { kind: "password_change"; changeToken: string; expiresAt: number | null };

export interface TotpEnrollment {
  /** Base32 secret — shown once for manual entry. */
  secret: string;
  otpauthUri: string;
  /** Inline SVG of the QR code (rendered as an image, never as HTML). */
  qrSvg: string;
}

export interface EnrollmentResult {
  recoveryCodes: string[];
  /** Present when enrolment finished a login (pre-login enrolment). */
  session: Session | null;
}

export interface PasswordChangeInput {
  currentPassword: string;
  newPassword: string;
  /** First login with a temporary password (from the login step). */
  changeToken?: string;
}

export interface StaffSessionInfo {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  current: boolean;
}

/**
 * Boundary to identity-service (T01). Implementations: dev bypass (`auth-api.ts`), the real API
 * (`features/auth/session.ts`), fakes in tests.
 */
/** T01-18: the person an invitation e-mail was sent to. */
export interface InviteInfo {
  email: string;
  name: string;
  /** epoch ms the link stops working */
  expiresAt: number | null;
  /** the role needs two-step sign-in — the page says an authenticator app comes next */
  requiresTotp: boolean;
}

export interface AuthApi {
  /**
   * The dev bypass keeps its session in sessionStorage (so reloads survive without a backend);
   * the real API keeps the access token in memory only and restores it with the refresh cookie.
   */
  readonly persistSession?: boolean;
  signIn(credentials: Credentials): Promise<LoginStep>;
  verifyMfa(mfaToken: string, code: string): Promise<Session>;
  /** `enrollToken` from the login step, or `null` + `session` for a signed-in staff member. */
  startEnrollment(enrollToken: string | null, session?: Session | null): Promise<TotpEnrollment>;
  confirmEnrollment(
    enrollToken: string | null,
    code: string,
    session?: Session | null,
  ): Promise<EnrollmentResult>;
  /** Returns the next login step for a forced change, `null` for a normal change. */
  changePassword(input: PasswordChangeInput, session?: Session | null): Promise<LoginStep | null>;
  forgotPassword(email: string): Promise<void>;
  resetPassword(token: string, newPassword: string): Promise<void>;
  /** T01-18: who the invitation link is for (nothing used up); `challenge_expired` when not valid. */
  checkInvite(token: string): Promise<InviteInfo>;
  /** T01-18: first password with the invitation link → next login step (two-step setup / session). */
  acceptInvite(token: string, newPassword: string): Promise<LoginStep>;
  /** Silent refresh with the HttpOnly cookie; `null` = no (more) session. */
  refresh(): Promise<Session | null>;
  signOut(session: Session): Promise<void>;
  listSessions(session: Session): Promise<StaffSessionInfo[]>;
  /** Sign out every other device; resolves how many were signed out. */
  revokeOtherSessions(session: Session): Promise<number>;
}

export type AuthErrorKey =
  | "invalid"
  | "not_available"
  | "locked"
  | "rate_limited"
  | "code_invalid"
  | "challenge_expired"
  | "weak_password"
  | "network"
  | "unknown";

export class AuthError extends Error {
  constructor(
    /** i18n key under `auth:errors.*`. */
    public readonly key: AuthErrorKey,
    /** Seconds until the next try is allowed (lockout / rate limit). */
    public readonly retryAfter: number | null = null,
  ) {
    super(key);
    this.name = "AuthError";
  }
}

export type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; session: Session }
  /** The session could not be refreshed: the screen stays (drafts kept) under a sign-in prompt. */
  | { status: "expired"; session: Session };

export function hasPermission(session: Session | null, permission: Permission): boolean {
  if (!session) {
    return false;
  }
  return session.user.permissions.includes(permission);
}

/** Canonical permissions out of a token/storage payload; unknown strings are dropped, aliases resolved. */
export function normalizePermissions(values: readonly string[]): Permission[] {
  const out: Permission[] = [];
  for (const value of values) {
    const canonical = resolvePermission(value);
    if (canonical && !out.includes(canonical)) {
      out.push(canonical);
    }
  }
  return out;
}

export function isExpired(session: Session, now = Date.now()): boolean {
  return session.expiresAt <= now;
}
